import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Upload, Send, AlertCircle, MapPin, Info, Globe, Users, AlertTriangle, Star, MessageSquare } from 'lucide-react';

const REGINA_WARDS = [
  { id: '', name: 'Select a Ward (Optional)' },
  { id: 'Ward 1', name: 'Ward 1 - Arnheim Place / North Central' },
  { id: 'Ward 2', name: 'Ward 2 - Rosemont / Mount Royal' },
  { id: 'Ward 3', name: 'Ward 3 - Cathedral / Core / Downtown' },
  { id: 'Ward 4', name: 'Ward 4 - Dewdney East / Eastview' },
  { id: 'Ward 5', name: 'Ward 5 - Albert Park / Lakeview' },
  { id: 'Ward 6', name: 'Ward 6 - Normanview / Sherwood Estates' },
  { id: 'Ward 7', name: 'Ward 7 - Harbour Landing / Whitmore Park' },
  { id: 'Ward 8', name: 'Ward 8 - Hillsdale / Glencairn' },
  { id: 'Ward 9', name: 'Ward 9 - Wascana View / The Creeks' },
  { id: 'Ward 10', name: 'Ward 10 - Arcola East / Greens on Gardiner' },
  { id: 'Mayor', name: 'City-Wide / Mayor\'s Office' },
];

// Post categories for balanced community input
const POST_CATEGORIES = [
  { 
    id: 'issue', 
    label: 'Report an Issue', 
    icon: AlertTriangle, 
    color: 'red',
    description: 'Infrastructure problems, service issues',
    placeholder: 'Describe the issue you observed...'
  },
  { 
    id: 'positive', 
    label: 'Hidden Gem / Kudos', 
    icon: Star, 
    color: 'green',
    description: 'Good things, hidden gems, thank yous',
    placeholder: 'Share something positive about your community...'
  },
  { 
    id: 'discussion', 
    label: 'General Discussion', 
    icon: MessageSquare, 
    color: 'blue',
    description: 'Questions, ideas, community chat',
    placeholder: 'What would you like to discuss...'
  },
];

const Submit = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    alias: user?.username || '',
    content: '',
    address: '',
    ward: '',
    post_type: 'main_feed', // main_feed or ward_only
    category: 'issue', // issue, positive, discussion
    issue_category_id: '', // For structured issue reporting
    lat: null,
    lng: null,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lookingUpWard, setLookingUpWard] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [issueCategories, setIssueCategories] = useState([]);
  const [groupedCategories, setGroupedCategories] = useState({});

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);

  // Handle inline login
  const handleInlineLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error('Please enter email and password');
      return;
    }
    setLoginLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Logged in! You can now submit.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/issue-categories');
      setIssueCategories(res.data?.categories || []);
      setGroupedCategories(res.data?.grouped || {});
    } catch (e) {
      console.log('Issue categories not available');
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // When user selects "Ward Only", auto-set to their ward
  useEffect(() => {
    if (formData.post_type === 'ward_only' && user?.ward) {
      setFormData(prev => ({ ...prev, ward: user.ward }));
    }
  }, [formData.post_type, user?.ward]);

  // Get user's current location and reverse geocode
  const handleUseMyLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Call backend to reverse geocode
          const response = await api.get('/reverse-geocode', {
            params: { lat: latitude, lng: longitude }
          });
          
          if (response.data.success) {
            // Auto-fill the form
            setFormData(prev => ({
              ...prev,
              address: response.data.postal_code 
                ? `${response.data.address}, ${response.data.postal_code}`
                : response.data.address,
              ward: formData.post_type === 'ward_only' && user?.ward 
                ? user.ward  // Keep user's ward for ward_only posts
                : (response.data.ward || prev.ward),
              lat: latitude,
              lng: longitude
            }));
            
            toast.success('Location detected! Address auto-filled.');
          } else {
            toast.error(response.data.message || 'Location is outside Regina');
          }
        } catch (error) {
          console.error('Geocoding error:', error);
          toast.error('Failed to determine address from location');
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        let message = 'Failed to get your location';
        if (error.code === 1) message = 'Location permission denied';
        else if (error.code === 2) message = 'Location unavailable';
        else if (error.code === 3) message = 'Location request timeout';
        toast.error(message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Extract postal code from address and lookup ward
  const lookupWardFromAddress = async (address) => {
    if (!address || address.length < 5) return;
    // Don't override ward for ward_only posts
    if (formData.post_type === 'ward_only' && user?.ward) return;
    
    // Try to extract a postal code pattern (Canadian: A1A 1A1 or A1A1A1)
    const postalMatch = address.match(/[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d/);
    if (!postalMatch) return;
    
    const postalCode = postalMatch[0].replace(/\s/g, '').toUpperCase();
    setLookingUpWard(true);
    
    try {
      const response = await api.get(`/ward-lookup/${postalCode}`);
      // If we get a valid ward back, auto-populate it
      if (response.data && response.data.ward) {
        setFormData(prev => ({ ...prev, ward: response.data.ward }));
        toast.success(`Ward auto-detected: ${response.data.ward}`);
      }
    } catch (error) {
      // Silently fail - user can still manually select ward
      console.log('Ward lookup failed, user can select manually');
    } finally {
      setLookingUpWard(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be under 5MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please log in to submit a record');
      navigate('/login');
      return;
    }

    if (!user.is_verified) {
      toast.error('Please verify your email before submitting');
      return;
    }

    if (formData.content.length < 10) {
      toast.error('Description must be at least 10 characters');
      return;
    }

    // For ward_only posts, user must have a ward
    if (formData.post_type === 'ward_only' && !user?.ward) {
      toast.error('Please set your ward in Dashboard to post to ward community');
      return;
    }

    setSubmitting(true);

    try {
      let imageUrl = null;

      // Upload image if present
      if (imageFile) {
        const formDataImg = new FormData();
        formDataImg.append('file', imageFile);
        const uploadRes = await api.post('/posts/upload-image', formDataImg, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadRes.data.image_url;
      }

      // Create post
      await api.post('/posts', {
        content: formData.content,
        address: formData.address || null,
        ward: formData.post_type === 'ward_only' ? user.ward : (formData.ward || null),
        lat: formData.lat || null,
        lng: formData.lng || null,
        image_url: imageUrl,
        post_type: formData.post_type,
        category: formData.category,
        issue_category_id: formData.category === 'issue' ? formData.issue_category_id : null,
      });

      // Success message based on category
      let successMsg = 'Posted successfully!';
      if (formData.post_type === 'ward_only') {
        successMsg = 'Posted to your ward community!';
      } else if (formData.category === 'positive') {
        successMsg = 'Thanks for sharing something positive! 🌟';
      } else if (formData.category === 'issue') {
        successMsg = 'Issue reported! Others can verify it.';
      }
      
      toast.success(successMsg);
      navigate(formData.post_type === 'ward_only' ? '/dashboard' : '/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit record';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Get the current category config
  const currentCategory = POST_CATEGORIES.find(c => c.id === formData.category) || POST_CATEGORIES[0];

  if (!user) {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-4 py-16">
          <div className="text-center mb-8">
            <h1 className="font-headline text-3xl text-white mb-2">
              LOG IN TO SUBMIT
            </h1>
            <p className="text-gray-400 text-sm">
              You must be logged in and verified to submit records.
            </p>
          </div>
          
          <form onSubmit={handleInlineLogin} className="space-y-4">
            <div>
              <label className="block font-mono text-xs text-[#C5A52A] tracking-wider mb-2 uppercase">
                Email
              </label>
              <input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                className="w-full px-4 py-3 bg-[#111] border border-zinc-800 text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label className="block font-mono text-xs text-[#C5A52A] tracking-wider mb-2 uppercase">
                Password
              </label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                className="w-full px-4 py-3 bg-[#111] border border-zinc-800 text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-[#C5A52A] hover:bg-[#D4B93A] disabled:bg-[#C5A52A]/50 text-black font-display text-sm py-3 tracking-wider transition-colors"
            >
              {loginLoading ? 'LOGGING IN...' : 'LOG IN & CONTINUE'}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              Don't have an account?{' '}
              <button
                onClick={() => navigate('/register')}
                className="text-[#C5A52A] hover:underline"
              >
                Register here
              </button>
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="text-center mb-8">
          <h1 className="font-headline text-4xl md:text-5xl text-white mb-2">
            SHARE WITH YOUR COMMUNITY
          </h1>
          <p className="font-mono text-xs text-gray-500 tracking-[0.2em] uppercase">
            Report issues, share discoveries, or start a discussion
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Post Category Selector */}
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-4">
            <label className="block font-mono text-xs text-[#C5A52A] tracking-wider mb-3 uppercase">
              What are you sharing?
            </label>
            <div className="grid grid-cols-3 gap-2">
              {POST_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const isSelected = formData.category === cat.id;
                const colorClasses = {
                  red: isSelected ? 'bg-red-900/30 border-red-500 text-red-400' : '',
                  green: isSelected ? 'bg-green-900/30 border-green-500 text-green-400' : '',
                  blue: isSelected ? 'bg-blue-900/30 border-blue-500 text-blue-400' : '',
                };
                
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, category: cat.id })}
                    className={`p-3 rounded border transition-all flex flex-col items-center gap-1 ${
                      isSelected
                        ? colorClasses[cat.color]
                        : 'bg-[#111] border-[#2a2a2a] text-gray-500 hover:border-gray-500'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-display text-[10px] tracking-wider text-center leading-tight">{cat.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-gray-600 text-xs font-mono text-center">
              {currentCategory.description}
            </p>
          </div>

          {/* Issue Type Dropdown (only for issue reports) */}
          {formData.category === 'issue' && Object.keys(groupedCategories).length > 0 && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-4">
              <label className="block font-mono text-xs text-red-400 tracking-wider mb-2 uppercase">
                Issue Type (Optional)
              </label>
              <select
                value={formData.issue_category_id}
                onChange={(e) => setFormData({ ...formData, issue_category_id: e.target.value })}
                className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-red-500 focus:outline-none transition-colors"
              >
                <option value="">-- Select Issue Type --</option>
                {Object.entries(groupedCategories).map(([group, cats]) => (
                  <optgroup key={group} label={group}>
                    {cats.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="font-mono text-[10px] text-gray-600 mt-1">
                Selecting a type helps track and verify issues by category
              </p>
            </div>
          )}

          {/* Post Type Selector (Main Feed vs Ward Only) */}
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-4">
            <label className="block font-mono text-xs text-[#C5A52A] tracking-wider mb-3 uppercase">
              Where to Post
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, post_type: 'main_feed' })}
                className={`p-4 rounded border transition-all flex flex-col items-center gap-2 ${
                  formData.post_type === 'main_feed'
                    ? 'bg-[#C5A52A]/20 border-[#C5A52A] text-[#C5A52A]'
                    : 'bg-[#111] border-[#2a2a2a] text-gray-500 hover:border-gray-500'
                }`}
              >
                <Globe className="w-6 h-6" />
                <span className="font-display text-xs tracking-wider">MAIN FEED</span>
                <span className="font-mono text-[10px] text-gray-500">Visible to everyone</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, post_type: 'ward_only' })}
                className={`p-4 rounded border transition-all flex flex-col items-center gap-2 ${
                  formData.post_type === 'ward_only'
                    ? 'bg-blue-900/30 border-blue-500 text-blue-400'
                    : 'bg-[#111] border-[#2a2a2a] text-gray-500 hover:border-gray-500'
                }`}
              >
                <Users className="w-6 h-6" />
                <span className="font-display text-xs tracking-wider">WARD ONLY</span>
                <span className="font-mono text-[10px] text-gray-500">
                  {user?.ward ? `Posts to ${user.ward}` : 'Your neighbors only'}
                </span>
              </button>
            </div>
            {formData.post_type === 'ward_only' && !user?.ward && (
              <p className="mt-3 text-yellow-500 text-xs font-mono">
                ⚠️ Set your ward in Dashboard to post to ward community
              </p>
            )}
            {formData.post_type === 'ward_only' && user?.ward && (
              <p className="mt-3 text-blue-400 text-xs font-mono">
                ✓ This will post only to <strong>{user.ward}</strong> residents
              </p>
            )}
          </div>

          {/* Identity Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Public Identity */}
            <div>
              <label className="block font-mono text-xs text-[#C5A52A] tracking-wider mb-2 uppercase">
                Public Identity
              </label>
              <input
                type="text"
                value={formData.alias || user?.username || ''}
                disabled
                className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-gray-400 font-mono text-sm focus:outline-none cursor-not-allowed"
                placeholder="Constituent Alias"
              />
            </div>

            {/* Contact Email (Hidden) */}
            <div>
              <label className="flex items-center gap-2 font-mono text-xs text-[#C5A52A] tracking-wider mb-2 uppercase">
                Contact Email
                <AlertCircle className="w-3 h-3 text-red-500" />
              </label>
              <input
                type="text"
                value="Verified residency only"
                disabled
                className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-gray-600 font-mono text-sm focus:outline-none cursor-not-allowed"
              />
              <p className="font-mono text-[10px] text-gray-600 mt-1 tracking-wider uppercase">
                Never shown on public feed. Used for legal verifications.
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block font-mono text-xs text-[#C5A52A] tracking-wider mb-2 uppercase">
              {formData.category === 'issue' ? 'Describe the Issue' : 
               formData.category === 'positive' ? 'Share Your Discovery' : 
               'Your Message'} (Min. 10 chars)
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
              rows={6}
              className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none resize-none transition-colors"
              placeholder={currentCategory.placeholder}
            />
          </div>

          {/* Location Section */}
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-[#C5A52A]" />
              <span className="font-mono text-xs text-[#C5A52A] tracking-wider uppercase">Location Details</span>
              {formData.category !== 'issue' && (
                <span className="text-gray-600 text-xs font-mono">(Optional)</span>
              )}
            </div>
            
            {formData.category === 'issue' && (
              <div className="bg-red-900/20 border border-red-900/50 p-3 rounded mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-red-400 text-xs font-mono">
                    Adding a location helps track issues and allows others to verify them.
                  </p>
                </div>
              </div>
            )}

            {/* Use My Location Button */}
            <button
              type="button"
              onClick={handleUseMyLocation}
              disabled={gettingLocation}
              className="w-full mb-4 bg-green-900/30 hover:bg-green-900/50 disabled:bg-green-900/20 border border-green-500 text-green-400 font-mono text-sm py-3 px-4 rounded transition-colors flex items-center justify-center gap-2"
            >
              <MapPin className={`w-4 h-4 ${gettingLocation ? 'animate-pulse' : ''}`} />
              {gettingLocation ? 'Getting your location...' : 'Use My Current Location'}
            </button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-800"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-[#0a0a0a] px-2 text-gray-600 font-mono">OR ENTER MANUALLY</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Address/Intersection */}
              <div>
                <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                  Address or Intersection
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  onBlur={(e) => lookupWardFromAddress(e.target.value)}
                  className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none transition-colors"
                  placeholder="e.g., 1234 Victoria Ave, S4P 0T1"
                />
                <p className="font-mono text-[10px] text-gray-600 mt-1">
                  Include postal code to auto-detect ward
                </p>
              </div>

              {/* Ward Dropdown */}
              <div>
                <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                  Ward {lookingUpWard && <span className="text-[#C5A52A]">(detecting...)</span>}
                </label>
                {formData.post_type === 'ward_only' && user?.ward ? (
                  // Locked to user's ward for ward_only posts
                  <div className="w-full px-4 py-3 bg-blue-900/20 border border-blue-500 text-blue-400 font-mono text-sm rounded">
                    {user.ward} <span className="text-blue-600">(your ward)</span>
                  </div>
                ) : (
                  <select
                    value={formData.ward}
                    onChange={(e) => setFormData({ ...formData, ward: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none transition-colors appearance-none cursor-pointer"
                  >
                    {REGINA_WARDS.map((ward) => (
                      <option key={ward.id} value={ward.id} className="bg-[#111]">
                        {ward.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

          {/* Evidence Upload */}
          <div>
            <label className="block font-mono text-xs text-[#C5A52A] tracking-wider mb-2 uppercase">
              {formData.category === 'issue' ? 'Evidence Photo' : 'Attach Photo'} (Optional)
            </label>
            <div 
              className="border-2 border-dashed border-[#2a2a2a] hover:border-[#3a3a3a] rounded p-8 text-center cursor-pointer transition-colors"
              onClick={() => document.getElementById('image-upload').click()}
            >
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-64 mx-auto rounded"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                    className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                  <p className="font-mono text-xs text-gray-500 tracking-wider uppercase">
                    {formData.category === 'issue' ? 'Attach Evidence (Max 5MB)' : 'Attach Photo (Max 5MB)'}
                  </p>
                </>
              )}
            </div>
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className={`w-full font-display text-sm py-4 tracking-wider transition-colors flex items-center justify-center gap-2 ${
              formData.category === 'positive'
                ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-600/50 text-white'
                : formData.category === 'discussion'
                  ? 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white'
                  : 'bg-[#C5A52A] hover:bg-[#D4B93A] disabled:bg-[#C5A52A]/50 text-black'
            }`}
            data-testid="commit-record-btn"
          >
            <Send className="w-4 h-4" />
            {submitting ? 'POSTING...' : 
             formData.category === 'positive' ? 'SHARE SOMETHING POSITIVE' :
             formData.category === 'discussion' ? 'START DISCUSSION' :
             'REPORT ISSUE'}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default Submit;
